// src/pages/DynamicQuestionDriver.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuestionnaire } from '../../contexts/QuestionnaireContext.jsx';
import { useAuth } from '../../contexts/AuthContext';
import QuestionLayout from '../../components/Layout/QuestionLayout.jsx';
import StepsNav from '../../components/Navigation/StepsNav.jsx';

import { componentMap } from '../../utils/componentMapping.jsx';
import { modalMap } from '../../utils/modalMapping.jsx';
import { API_BASE_URL, ANIMATION_DURATION, getDefaultStyles, transitionStylesPerPhase } from '../../utils/constants.jsx';
import useQuizData from '../../hooks/useQuizData.jsx';

const DynamicQuestionDriver = (props) => {
    const { targetQuizName, expectedRole, baseRoute } = props;
    const { stepKeyParam } = useParams();
    const navigate = useNavigate();
    const { getAnswer, updateAnswer, getAllAnswers, clearAllAnswers } = useQuestionnaire();
    const { isAuthenticated, role, userId, token, logout, isLoading: isAuthLoading } = useAuth();

    const { quizData, isLoadingQuiz, quizLoadError } = useQuizData(targetQuizName, token); // Use the custom hook

    const [currentQuestionSchema, setCurrentQuestionSchema] = useState(null);
    const [currentQuestionId, setCurrentQuestionId] = useState(null);
    const [currentAnswer, setCurrentAnswer] = useState(undefined);
    const [validationError, setValidationError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const prevStepKeyParamRef = useRef();
    const [activeModalType, setActiveModalType] = useState(null);
    const [animationDirection, setAnimationDirection] = useState('forward');
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const nodeRefs = useRef({});
    const [scheduleCallLink, setScheduleCallLink] = useState(null);

    useEffect(() => {
        if (isLoadingQuiz || !quizData || quizLoadError) {
            return;
        }

        const questionSchema = quizData.questions?.find(q => q.id === stepKeyParam);

        if (questionSchema) {
            console.log(questionSchema)
            setCurrentQuestionSchema(questionSchema);
            setCurrentQuestionId(questionSchema.id);
            const existingAnswer = getAnswer(questionSchema.id);
            let initialAnswer = questionSchema.initialValue;
            // For WithOther types, ensure the correct keys from initialValue are used
            if (questionSchema.type.includes("WithOther") || questionSchema.type === "checkboxGroupWithOther" || questionSchema.type === "selectableOptionWithOther") {
                let selectionsKey = Object.keys(initialAnswer || {}).find(k => Array.isArray(initialAnswer[k])) || 'selections';
                let otherDetailKey = Object.keys(initialAnswer || {}).find(k => typeof initialAnswer[k] === 'string' && k !== selectionsKey) || 'otherDetail';
                if (typeof initialAnswer !== 'object' || initialAnswer === null) {
                    initialAnswer = { [selectionsKey]: [], [otherDetailKey]: '' };
                } else {
                    // Ensure both keys exist
                    initialAnswer = { [selectionsKey]: initialAnswer[selectionsKey] || [], [otherDetailKey]: initialAnswer[otherDetailKey] || '' };
                }
            }
            // Special handling for numberInput: extract .amount if initialValue is an object
            if (questionSchema.type === "numberInput") {
                if (
                    initialAnswer &&
                    typeof initialAnswer === "object" &&
                    initialAnswer !== null &&
                    Object.prototype.hasOwnProperty.call(initialAnswer, "amount")
                ) {
                    initialAnswer = initialAnswer.amount;
                }
            }
            setCurrentAnswer(existingAnswer !== undefined ? existingAnswer : initialAnswer);
            setValidationError('');
        } else {
            console.warn(`DQD: No schema for stepKey "${stepKeyParam}". Navigating to first question.`);
            // Or, navigate to the first question of the loaded quiz if available:
            if (quizData.questions && quizData.questions.length > 0) {
                navigate(`${baseRoute}${quizData.questions[0].id}`, { replace: true });
            } else {
                navigate('/questions/step1_1', { replace: true }); // Fallback if no questions are found
            }
        }
    }, [stepKeyParam, getAnswer, navigate, quizData, isLoadingQuiz, quizLoadError, baseRoute]);

    useEffect(() => {
        if (prevStepKeyParamRef.current !== stepKeyParam) {
            setShowModal(false);
            setActiveModalType(null);
        }
        prevStepKeyParamRef.current = stepKeyParam;
    }, [stepKeyParam]);

    useEffect(() => {
        if (pendingNavigation && pendingNavigation.direction === animationDirection) {
            console.log(`EFFECT (Stage 2): Navigating to ${pendingNavigation.path} with confirmed direction ${animationDirection}`);
            navigate(pendingNavigation.path);
            setPendingNavigation(null);
        }
    }, [animationDirection, pendingNavigation, navigate]);

    const initiateNavigation = (targetPath, direction) => {
        console.log(`INITIATE NAV: Target: ${targetPath}, Desired Direction: ${direction}. Current animationDirection: ${animationDirection}`);
        setAnimationDirection(direction);
        setPendingNavigation({ path: targetPath, direction: direction });
    };

    useEffect(() => {
        if (currentQuestionSchema) {
            console.log(`DQD_DEBUG (${currentQuestionSchema.id}): currentAnswer changed to:`, currentAnswer);
        } else {
            console.log(`DQD_DEBUG: currentAnswer changed (no schema yet):`, currentAnswer);
        }
    }, [currentAnswer, currentQuestionSchema]);


    const handleAnswerChange = useCallback((value, error = '') => {
        // Ensure value is an object with correct keys for WithOther types
        if (currentQuestionSchema && (currentQuestionSchema.type.includes("WithOther") || currentQuestionSchema.type === "checkboxGroupWithOther" || currentQuestionSchema.type === "selectableOptionWithOther")) {
            let initialValue = currentQuestionSchema.initialValue || {};
            let selectionsKey = Object.keys(initialValue).find(k => Array.isArray(initialValue[k])) || 'selections';
            let otherDetailKey = Object.keys(initialValue).find(k => typeof initialValue[k] === 'string' && k !== selectionsKey) || 'otherDetail';
            if (typeof value !== 'object' || value === null) {
                value = { [selectionsKey]: [], [otherDetailKey]: '' };
            } else {
                // Remap keys if needed
                let newValue = {};
                newValue[selectionsKey] = value[selectionsKey] || value.selections || [];
                newValue[otherDetailKey] = value[otherDetailKey] || value.otherDetail || '';
                value = newValue;
            }
        }
        setCurrentAnswer(value);
        setValidationError(error);
        if (currentQuestionSchema) {
            updateAnswer(currentQuestionSchema.id, value);
        }
    }, [currentQuestionSchema, updateAnswer]);
    const processNavigationLogic = (navLogic, isNextDirection) => {
        if (!navLogic) return null;
        if (navLogic.conditions) {
            for (const condition of navLogic.conditions) {
                let conditionMet = false;
                const answerToEvaluate = condition.dependentQuestionId ? getAnswer(condition.dependentQuestionId) : currentAnswer;
                let valueToCompare = answerToEvaluate;

                if (condition.dependentQuestionId && condition.dependentQuestionAnswerKey && typeof answerToEvaluate === 'object' && answerToEvaluate !== null) {
                    valueToCompare = answerToEvaluate[condition.dependentQuestionAnswerKey];
                } else if (!condition.dependentQuestionId && condition.answerKey && typeof answerToEvaluate === 'object' && answerToEvaluate !== null) {
                    valueToCompare = answerToEvaluate[condition.answerKey];
                }

                if (valueToCompare === (condition.dependentQuestionId ? condition.dependentQuestionAnswer : condition.answer)) {
                    conditionMet = true;
                }

                if (conditionMet) {
                    return isNextDirection ? condition.nextStepId : condition.previousStepId;
                }
            }
        }
        return isNextDirection ? navLogic.defaultNextStepId : navLogic.defaultPreviousStepId;
    };

    const handleNext = async () => {
        if (!currentQuestionSchema) return;

        try {
            updateAnswer(currentQuestionSchema.id, currentAnswer);
        } catch (error) {
            console.error("DQD: Error during updateAnswer in handleNext:", error);
            alert("An error occurred while attempting to save your answer. Please try again.");
            return;
        }

        const nav = currentQuestionSchema.navigation;
        let nextTarget = nav.nextStepId;

        if (nav.nextStepLogic) {
            const logic = nav.nextStepLogic;
            if (logic.action === "submitAndShowModal" || logic.action === "submitAltAndShowModal" || logic.action === "homeSaveAndNavigate") {
                const allAnswers = getAllAnswers();
                console.log("DQD: Attempting to submit all answers:", allAnswers);

                const authToken = token;

                if (!authToken || !userId) {
                    console.error("DQD: No auth token or userId found. Cannot submit answers.", { authToken: !!authToken, userId: !!userId });
                    alert("Error de autenticación. Por favor, inicia sesión de nuevo.");
                    initiateNavigation('/login', 'forward');
                    return;
                }

                try {
                    let submitUrl = '';
                    let requestBody = {};
                    let httpMethod = 'PUT'; // Default for user profile update

                    if (logic.action === "submitAndShowModal") {
                        submitUrl = `${API_BASE_URL}/api/users/${userId}`;
                        requestBody = { initial_form_json: allAnswers, completed: 1 };
                    } else if (logic.action === "submitAltAndShowModal") {
                        const userTripsUrl = `${API_BASE_URL}/api/user-trips`;
                        const getResponse = await fetch(userTripsUrl, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`,
                            },
                        });
                        const userTripsData = await getResponse.json();

                        let lastTrip = null;
                        for (const item of userTripsData) {
                            if (item.user_id === userId) {
                                if (!lastTrip || item.id > lastTrip.id) {
                                    lastTrip = item;
                                }
                            }
                        }

                        if (lastTrip === null || lastTrip.completed === 1) {
                            submitUrl = userTripsUrl;
                            httpMethod = 'POST';
                            requestBody = { answer: allAnswers, completed: 1, user_id: userId };
                        } else {
                            submitUrl = `${API_BASE_URL}/api/user-trips/${lastTrip.id}`;
                            httpMethod = 'PUT';
                            requestBody = { answer: allAnswers, completed: 1, user_id: userId };
                        }
                    } else if (logic.action === "homeSaveAndNavigate") {

                        const supplierProfilesUrl = `${API_BASE_URL}/api/supplier-profiles`;
                        const getResponse = await fetch(supplierProfilesUrl, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`,
                            },
                        });
                        const supplierProfilesData = await getResponse.json();

                        let lastProfile = null;
                        for (const item of supplierProfilesData) {
                            if (item.supplier_id === parseInt(userId)) {
                                if (!lastProfile || item.id > lastProfile.id) {
                                    lastProfile = item;
                                }
                            }
                        }

                        if (lastProfile === null) {
                            submitUrl = supplierProfilesUrl;
                            httpMethod = 'POST';
                            requestBody = { answer: allAnswers, completed: 1, supplier_id: userId, type: "Pre-evaluación" };
                        } else {
                            submitUrl = `${API_BASE_URL}/api/supplier-profiles/${lastProfile.id}`; // Corrected to use profile ID for PUT
                            httpMethod = 'PUT';
                            requestBody = { answer: allAnswers, completed: 1, supplier_id: userId, type: "Sello Checkin" };
                        }

                    }

                    console.log(`DQD: Submitting to URL: ${submitUrl} with method: ${httpMethod}`);
                    const response = await fetch(submitUrl, {
                        method: httpMethod,
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authToken}`,
                        },
                        body: JSON.stringify(requestBody),
                    });

                    const responseData = await response.json();

                    if (!response.ok) {
                        const errorMessage = responseData.error?.message || responseData.message || `Fallo al enviar respuestas: ${response.status}`;
                        throw new Error(errorMessage);
                    }

                    console.log('DQD: Respuestas enviadas exitosamente:', responseData);
                    clearAllAnswers();
                    console.log('DQD: All answers cleared from context and localStorage after successful submission.');

                    if (logic.action === "submitAndShowModal" || logic.action === "submitAltAndShowModal") {
                        if (logic.onScheduleCallLink) {
                            setScheduleCallLink(logic.onScheduleCallLink);
                        } else {
                            setScheduleCallLink(null);
                        }
                        setActiveModalType(logic.modalType);
                        setShowModal(true);
                    } else if (logic.action === "homeSaveAndNavigate") {
                        initiateNavigation(logic.path || '/home', 'forward');
                    }
                    return;

                } catch (error) {
                    console.error('DQD: Error al enviar respuestas del cuestionario:', error.message);
                    alert(`Hubo un error al enviar tus respuestas: ${error.message}. Por favor, inténtalo de nuevo.`);
                    return;
                }
            } else if (logic.action === "showModal") {
                setActiveModalType(logic.modalType);
                setShowModal(true);
                return;
            } else if (logic.action === "homeAndNavigate") {
                initiateNavigation(logic.path || '/home', 'forward');
                return;
            } else if (logic.action === "logoutAndNavigate") {
                logout();
                initiateNavigation(logic.path || '/login', 'forward');
                return;
            }
            const conditionalNext = processNavigationLogic(logic, true);
            if (conditionalNext) nextTarget = conditionalNext;
        }

        if (nextTarget) {
            initiateNavigation(`${baseRoute}${nextTarget}`, 'forward');
        } else if (!nav.nextStepLogic || (nav.nextStepLogic && !["submitAndShowModal", "submitAltAndShowModal", "showModal", "logoutAndNavigate", "homeSaveAndNavigate"].includes(nav.nextStepLogic.action))) {
            console.warn("DQD: No next step defined or action taken for", currentQuestionSchema.id);
        }
    };

    const handlePrevious = () => {
        if (!currentQuestionSchema) return;

        const nav = currentQuestionSchema.navigation;
        console.log(nav)
        let prevTarget = nav.previousStepId;

        if (nav.previousStepLogic) {
            const logic = nav.previousStepLogic;
            if (logic.action === "logoutAndNavigate") {
                logout();
                initiateNavigation(logic.path || '/login', 'backward');
                return;
            }
            if (logic.action === "homeAndNavigate") {
                initiateNavigation(logic.path || '/login', 'backward');
                return;
            }
            if (logic.branchingOn) {
                const branchingAnswerFull = getAnswer(logic.branchingOn);
                let answerToTest = branchingAnswerFull;
                if (logic.branchingOnAnswerKey && typeof branchingAnswerFull === 'object' && branchingAnswerFull !== null) {
                    answerToTest = branchingAnswerFull[logic.branchingOnAnswerKey];
                }
                if (answerToTest === logic.branchingSkipIfAnswerEquals) {
                    prevTarget = logic.branchingSkipToStepId;
                } else {
                    prevTarget = logic.defaultPreviousStepId || prevTarget;
                }
            } else {
                const conditionalPrev = processNavigationLogic(logic, false);
                if (conditionalPrev) prevTarget = conditionalPrev;
            }
        }

        if (prevTarget) {
            initiateNavigation(`${baseRoute}${prevTarget}`, 'backward');
        } else {
            console.warn("DQD: No previous step defined for", currentQuestionSchema.id);
        }
    };

    const handleModalClose = () => {
        const modalConfig = currentQuestionSchema?.modals?.[activeModalType];
        setShowModal(false);
        setActiveModalType(null);

        if (modalConfig?.onHideAction === "clearAnswersAndNavigate") {
            clearAllAnswers();
            initiateNavigation(modalConfig.onHideNavigateTo || '/', animationDirection);
        } else if (modalConfig?.onHideAction === "navigate") {
            initiateNavigation(modalConfig.onHideNavigateTo || '/', animationDirection);
        }
    };

    const handleScheduleCallFromModal = useCallback(() => {
        if (scheduleCallLink) {
            window.open(scheduleCallLink, '_blank');
        }
    }, [scheduleCallLink]);

    const modalCallbacks = {
        onRegister: () => {
            const modalConfig = currentQuestionSchema?.modals?.[activeModalType];
            setShowModal(false); setActiveModalType(null);
            if (modalConfig?.onRegisterNavigateTo) initiateNavigation(modalConfig.onRegisterNavigateTo, 'forward');
        },
        onLogin: () => {
            const modalConfig = currentQuestionSchema?.modals?.[activeModalType];
            setShowModal(false); setActiveModalType(null);
            if (modalConfig?.onLoginNavigateTo) initiateNavigation(modalConfig.onLoginNavigateTo, 'forward');
        },
        onScheduleCall: () => {
            const modalConfig = currentQuestionSchema?.modals?.[activeModalType];
            if (modalConfig?.onScheduleCallLink) window.open(modalConfig.onScheduleCallLink, '_blank');
        },
    };

    if (isLoadingQuiz) return <div>Loading questionnaire...</div>;
    if (quizLoadError) return <div>Error loading questionnaire: {quizLoadError}. Please try again later.</div>;
    if (!quizData) return <div>No questionnaire data found.</div>;

    if (!currentQuestionSchema) {
        return <div>Loading question schema...</div>;
    }

    const defaultContentColumnClass = currentQuestionSchema.layout?.contentColumnClassName || "col-lg-8 col-md-10 mx-auto";
    const defaultFormContainerClass = currentQuestionSchema.layout?.formContainerClassName || "card shadow-sm p-4 p-md-5 my-4";

    if (isAuthLoading) {
        return <div>Loading authentication...</div>;
    }

    if (currentQuestionSchema.meta?.protected) {
        if (!isAuthenticated) {
            return <Navigate to="/login" replace />;
        }
        if (expectedRole && role !== expectedRole) {
            console.warn(`DQD: Role mismatch for protected content. Expected: ${expectedRole}, Actual: ${role}. Redirecting to login.`);
            return <Navigate to="/login" replace />;
        }
    }

    const InputComponent = componentMap[currentQuestionSchema.type];
    if (!InputComponent) {
        console.error(`DQD: No component mapped for question type: ${currentQuestionSchema.type}`);
        return <div>Error: Unknown question type. Contact support.</div>;
    }

    const ActiveModalComponent = activeModalType ? modalMap[activeModalType] : null;
    const activeModalConfig = activeModalType ? currentQuestionSchema.modals?.[activeModalType] : null;

    const { id: componentKey, ...schemaRest } = currentQuestionSchema;
    // Ensure "Other" option is present in options if needed
    let optionsWithOther = currentQuestionSchema.options || [];
    if (
        (currentQuestionSchema.type.includes("WithOther") || currentQuestionSchema.type === "checkboxGroupWithOther" || currentQuestionSchema.type === "selectableOptionWithOther") &&
        currentQuestionSchema.otherInputConfig &&
        currentQuestionSchema.otherInputConfig.optionValue &&
        !optionsWithOther.some(opt => opt.value === currentQuestionSchema.otherInputConfig.optionValue)
    ) {
        const otherLabel = currentQuestionSchema.otherInputConfig.optionLabel || "Otro";
        optionsWithOther = [
            ...optionsWithOther,
            {
                value: currentQuestionSchema.otherInputConfig.optionValue,
                label: otherLabel,
                text: otherLabel,
                id: `${currentQuestionSchema.id}-${currentQuestionSchema.otherInputConfig.optionValue}`,
            }
        ];
    }
    // Enforce required props for numberInput and numberInputWithToggle
    let schemaForInput = { id: componentKey, ...schemaRest };
    if (
        currentQuestionSchema.type === "numberInput" ||
        currentQuestionSchema.type === "numberInputWithToggle"
    ) {
        const enforcedNumberProps = {
            max: 20,
            min: 1,
            placeholder: "1",
            inputClassName: "form-control form-control-lg text-center",
        };
        schemaForInput = {
            ...schemaForInput,
            componentProps: {
                ...((schemaForInput && schemaForInput.componentProps) || {}),
                ...enforcedNumberProps,
            },
        };
    }

    const inputComponentProps = {
        id: currentQuestionSchema.id,
        schema: schemaForInput,
        value: currentAnswer,
        onChange: handleAnswerChange,
        otherInputConfig: currentQuestionSchema.otherInputConfig,
        toggleCheckboxConfig: currentQuestionSchema.toggleCheckboxConfig,
        options: optionsWithOther,
        sections: currentQuestionSchema.sections,
    };

    let isNextDisabled = false;
    const { validation, id: questionId, type: questionType } = currentQuestionSchema;

    console.log(`DQD_DEBUG (${questionId}): Validating with currentAnswer:`, currentAnswer, "Schema validation:", validation);

    if (validation?.blockOnValidationError !== false && validationError) {
        isNextDisabled = true;
    }

    if (!isNextDisabled && validation) {
        if (validation.required) {
            const answer = currentAnswer;
            if (answer === undefined || answer === null || answer === '' || (Array.isArray(answer) && answer.length === 0)) {
                isNextDisabled = true;
            } else if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
                if (
                    questionType.includes("WithOther") ||
                    questionType === "checkboxGroupWithOther" ||
                    questionType === "selectableOptionWithOther" ||
                    questionType === "multiSectionSelectableOption" ||
                    questionType === "multiSectionCheckboxGroup"
                ) {
                    const initialVal = currentQuestionSchema.initialValue || {};
                    let mainSelKey = Object.keys(initialVal).find(k => Array.isArray(initialVal[k]));
                    if (!mainSelKey) {
                        mainSelKey = 'selections';
                    }
                    const selectionsArray = (answer && answer[mainSelKey]) ? answer[mainSelKey] : [];
                    if (selectionsArray.length === 0) {
                        isNextDisabled = true;
                    }
                } else if (questionType === "numberInputWithToggle") {
                    if (answer.noFixedBudget === false && (answer.amount === undefined || answer.amount === '')) {
                        isNextDisabled = true;
                    }
                } else if (questionType === "dateRangePicker") {
                    const initialVal = currentQuestionSchema.initialValue || {};
                    const startDateKey = Object.keys(initialVal).find(k => k.toLowerCase().includes('start')) || 'startDate';
                    const endDateKey = Object.keys(initialVal).find(k => k.toLowerCase().includes('end')) || 'endDate';
                    if (!answer || !answer[startDateKey] || !answer[endDateKey]) {
                        isNextDisabled = true;
                    }
                }
            }
        }
        if (!isNextDisabled && validation.minSelections) {
            const answer = currentAnswer;
            let selectionsToCheck = [];
            if (Array.isArray(answer)) {
                selectionsToCheck = answer;
            } else if (typeof answer === 'object' && answer !== null && (
                questionType.includes("WithOther") ||
                questionType === "checkboxGroupWithOther" ||
                questionType === "selectableOptionWithOther" ||
                questionType === "multiSectionSelectableOption" ||
                questionType === "multiSectionCheckboxGroup"
            )) {
                const initialVal = currentQuestionSchema.initialValue || {};
                let mainSelKey = Object.keys(initialVal).find(k => Array.isArray(initialVal[k]));
                if (!mainSelKey) {
                    mainSelKey = 'selections';
                }
                selectionsToCheck = (answer && answer[mainSelKey]) ? answer[mainSelKey] : [];
            }
            if (selectionsToCheck.length < validation.minSelections) {
                isNextDisabled = true;
            }
        }
        if (!isNextDisabled &&
            questionType === "numberInput" &&
            currentAnswer !== undefined &&
            (validation.min !== undefined || validation.max !== undefined)) {
            const rawValue = String(currentAnswer);
            if (rawValue.trim() !== '' && rawValue !== 'undefined' && rawValue !== 'null') {
                const numValue = validation.integer ? parseInt(rawValue, 10) : parseFloat(rawValue);
                if (!isNaN(numValue)) {
                    if ((validation.min !== undefined && numValue < validation.min) || (validation.max !== undefined && numValue > validation.max)) {
                        isNextDisabled = true;
                    }
                }
            }
        }
        if (!isNextDisabled && validation.otherInputRequired && (questionType.includes("WithOther") || questionType === "checkboxGroupWithOther" || questionType === "selectableOptionWithOther")) {
            const answer = currentAnswer;
            const otherConfig = currentQuestionSchema.otherInputConfig;

            if (typeof answer === 'object' && answer !== null && otherConfig && otherConfig.optionValue) {
                const initialVal = currentQuestionSchema.initialValue || {};
                let mainSelKey = Object.keys(initialVal).find(k => Array.isArray(initialVal[k]));
                if (!mainSelKey) {
                    mainSelKey = 'selections';
                }
                let otherDetailLocalKey = Object.keys(initialVal).find(k => typeof initialVal[k] === 'string' && k !== mainSelKey);
                if (!otherDetailLocalKey) otherDetailLocalKey = 'otherDetail';

                const selectionsArray = (answer && answer[mainSelKey]) ? answer[mainSelKey] : [];
                const otherTextValue = (answer && answer[otherDetailLocalKey]) ? answer[otherDetailLocalKey] : "";

                if (selectionsArray.includes(otherConfig.optionValue) && otherTextValue.trim() === '') {
                    isNextDisabled = true;
                }
            }
        }
        if (!isNextDisabled && validation.mainInputRequiredIfNotToggled && questionType === "numberInputWithToggle") {
            if (typeof currentAnswer === 'object' && currentAnswer !== null && !currentAnswer.noFixedBudget && (currentAnswer.amount === undefined || currentAnswer.amount === '' || isNaN(parseFloat(currentAnswer.amount)))) {
                isNextDisabled = true;
            }
        }
    }

    let modalRenderContent = null;
    if (ActiveModalComponent && activeModalConfig && showModal) {
        try {
            modalRenderContent = (
                <ActiveModalComponent
                    show={showModal}
                    onClose={handleModalClose}
                    onHide={handleModalClose}
                    {...(modalCallbacks[activeModalType] || {})}
                    {...activeModalConfig}
                    onRegister={activeModalConfig.onRegisterNavigateTo ? modalCallbacks.onRegister : undefined}
                    onLogin={activeModalConfig.onLoginNavigateTo ? modalCallbacks.onLogin : undefined}
                    onScheduleCall={scheduleCallLink ? handleScheduleCallFromModal : undefined}
                />
            );
        } catch (error) {
            console.error("DQD: Error rendering modal:", activeModalType, error);
        }
    }

    if (stepKeyParam && !nodeRefs.current[stepKeyParam]) {
        nodeRefs.current[stepKeyParam] = React.createRef();
    }
    const currentQuestionNodeRef = stepKeyParam ? nodeRefs.current[stepKeyParam] : null;

    return (
        <>
            {quizData && Array.isArray(quizData.questions) && quizData.questions.length > 0 && (
                <StepsNav
                    currentStepName={currentQuestionSchema.layout?.stepName}
                    steps={(quizData.step_categories || '')
                        .split(',')
                        .map(s => s.trim())
                        .filter((stepName, index, self) => stepName.length > 0 && self.indexOf(stepName) === index)}
                />
            )}
            <div style={{ position: 'relative', flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <TransitionGroup component={null}>
                    {currentQuestionSchema && currentQuestionNodeRef && <Transition
                        key={stepKeyParam}
                        nodeRef={currentQuestionNodeRef}
                        timeout={ANIMATION_DURATION}
                        unmountOnExit
                        onEnter={(node, isAppearing) => {
                            console.log(`Transition: ${stepKeyParam} DIR: ${animationDirection} - onEnter. isAppearing: ${isAppearing}`);
                            if (node && node.style) {
                                void node.offsetHeight;
                            } else if (node) {
                                console.error(`[DQD] 'onEnter': node for ${stepKeyParam} is not a DOM element or 'style' property is missing. Ensure 'QuestionLayout' forwards the ref. Node:`, node);
                            } else {
                                console.warn(`[DQD] 'onEnter': node for ${stepKeyParam} is null or undefined.`);
                            }
                        }}
                        onEntering={(node, isAppearing) => console.log(`Transition: ${stepKeyParam} DIR: ${animationDirection} - onEntering. isAppearing: ${isAppearing}`)}
                        onEntered={(node, isAppearing) => console.log(`Transition: ${stepKeyParam} DIR: ${animationDirection} - onEntered. isAppearing: ${isAppearing}`)}
                        onExit={(node) => {
                            console.log(`Transition: ${stepKeyParam} DIR: ${animationDirection} - onExit`);
                            if (node && node.style) {
                                void node.offsetHeight;
                            } else if (node) {
                                console.error(`[DQD] 'onExit': node for ${stepKeyParam} is not a DOM element or 'style' property is missing. Node:`, node);
                            } else {
                                console.warn(`[DQD] 'onExit': node for ${stepKeyParam} is null or undefined.`);
                            }
                        }}
                        onExiting={(node) => console.log(`Transition: ${stepKeyParam} DIR: ${animationDirection} - onExiting`)}
                        onExited={(node) => console.log(`Transition: ${stepKeyParam} DIR: ${animationDirection} - onExited`)}
                    >
                        {state => (
                            <QuestionLayout
                                ref={currentQuestionNodeRef}
                                style={{
                                    ...getDefaultStyles(animationDirection),
                                    ...(transitionStylesPerPhase[state] ? transitionStylesPerPhase[state](animationDirection) : {})
                                }}
                                withLogo={currentQuestionSchema.layout?.withLogo !== undefined ? currentQuestionSchema.layout.withLogo : true}
                                progressText={currentQuestionSchema.layout?.progressText}
                                questionTitle={currentQuestionSchema.title}
                                questionDescription={currentQuestionSchema.subtitle}
                                answerOptionsComponent={InputComponent ? <InputComponent {...inputComponentProps} /> : <div>Component not found</div>}
                                onNext={handleNext}
                                onPrevious={handlePrevious}
                                isNextDisabled={isNextDisabled}
                                nextButtonText={currentQuestionSchema.navigation?.nextButtonText || "Continuar"}
                                previousButtonText={currentQuestionSchema.navigation?.previousButtonText || "Atrás"}
                                contentColumnClassName={defaultContentColumnClass}
                                formContainerClassName={defaultFormContainerClass}
                            />
                        )}
                    </Transition>}
                </TransitionGroup>
            </div>
            {modalRenderContent}
        </>
    );
};

export default DynamicQuestionDriver;